import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MonthlySummary from './pages/MonthlySummary';
import PrintBill from './pages/PrintBill';
import Admin from './pages/Admin';
import AdminCRUD from './pages/AdminCRUD';
import SetupPassword from './pages/SetupPassword';
import Help from './pages/Help';
import Maintenance from './pages/Maintenance';
import ReportsDashboard from './pages/reports/ReportsDashboard';
import SuspendedNotice from './pages/SuspendedNotice';
import AccessRestricted from './pages/AccessRestricted';
import WorkflowPage from './pages/workflow/WorkflowPage';
import PrintVisitSummary from './pages/PrintVisitSummary';
import PrintDischargeLetter from './pages/PrintDischargeLetter';
import PrintReferralLetter from './pages/PrintReferralLetter';
import PrintPreauthForm from './pages/PrintPreauthForm';
import ErrorBoundary from './components/ErrorBoundary';
import { Toaster } from 'sonner';
import useAutoLogout from './hooks/useAutoLogout';
import './App.css';

const API = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

// Global axios interceptor — if any API call returns 403 with a suspension
// message, redirect the user to the suspension warning page (do not log them out).
let suspensionHandled = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || '';
      if (status === 403 && error?.response?.data?.code === 'GEO_BLOCKED' && window.location.pathname !== '/restricted') {
        window.location.href = '/restricted';
        return Promise.reject(error);
      }
      if (status === 403 && /suspended/i.test(detail) && !suspensionHandled) {
        suspensionHandled = true;
        localStorage.setItem('user_status', 'Suspended');
        localStorage.setItem('suspension_message', detail);
        toast.error('Account suspended. Redirecting…', { duration: 4000 });
        setTimeout(() => {
          window.location.href = '/suspended';
        }, 800);
      }
    } catch (e) {
      // swallow — interceptor must not break the original error chain
    }
    return Promise.reject(error);
  }
);

// Wrapper component to check maintenance mode and auto-logout non-superadmin users
function MaintenanceCheck({ children }) {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  
  const token = localStorage.getItem('token');
  const username = localStorage.getItem('username');
  const role = localStorage.getItem('role');
  const hospitalName = localStorage.getItem('hospital_name');
  
  const isSuperAdmin = username === 'superadmin' || (role === 'Admin' && hospitalName === 'System Administration');
  
  // Auto logout function for maintenance mode
  const performMaintenanceLogout = () => {
    axios.post(`${API}/api/auth/logout`).catch(() => {});
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('hospital_name');
    toast.error('You have been logged out due to system maintenance.');
    navigate('/login');
  };
  
  // Clear all timers
  const clearTimers = () => {
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setCountdown(null);
  };
  
  useEffect(() => {
    const checkMaintenanceMode = async () => {
      try {
        const response = await axios.get(`${API}/api/settings/maintenance-mode`);
        const isMaintenanceOn = response.data.maintenance_mode;
        setMaintenanceMode(isMaintenanceOn);
        setMaintenanceMessage(response.data.message);
        
        // Handle maintenance mode auto-logout for non-superadmin users
        if (isMaintenanceOn && token && !isSuperAdmin) {
          // Start 2-minute countdown if not already started
          if (!logoutTimerRef.current) {
            const LOGOUT_DELAY = 2 * 60 * 1000; // 2 minutes in milliseconds
            setCountdown(120); // 120 seconds
            
            // Start countdown display
            countdownIntervalRef.current = setInterval(() => {
              setCountdown(prev => {
                if (prev <= 1) {
                  clearInterval(countdownIntervalRef.current);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
            
            // Set logout timer
            logoutTimerRef.current = setTimeout(() => {
              performMaintenanceLogout();
            }, LOGOUT_DELAY);
            
            toast.warning('System maintenance activated. You will be logged out in 2 minutes.', {
              duration: 10000
            });
          }
        } else {
          // Maintenance mode is OFF or user is superadmin - clear timers
          clearTimers();
        }
      } catch (error) {
        console.error('Failed to check maintenance mode:', error);
        setMaintenanceMode(false);
      } finally {
        setLoading(false);
      }
    };
    
    checkMaintenanceMode();
    
    // Check periodically (every 10 seconds for faster response)
    const interval = setInterval(checkMaintenanceMode, 10000);
    
    return () => {
      clearInterval(interval);
      clearTimers();
    };
  }, [token, isSuperAdmin, navigate]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => clearTimers();
  }, []);
  
  if (loading) {
    return null;
  }
  
  // If maintenance mode is ON and user is logged in but not superadmin
  if (maintenanceMode && token && !isSuperAdmin) {
    // Allow access to login page
    if (location.pathname === '/login' || location.pathname === '/') {
      return children;
    }
    
    // Show maintenance page with countdown
    return (
      <div className="relative">
        {countdown !== null && countdown > 0 && (
          <div className="fixed top-0 left-0 right-0 bg-red-600 text-white py-2 px-4 text-center z-50">
            <span className="font-semibold">
              ⚠️ System maintenance in progress. Auto-logout in: {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
            </span>
          </div>
        )}
        <Maintenance message={maintenanceMessage} />
      </div>
    );
  }
  
  return children;
}

function AppContent() {
  // Auto logout after 15 minutes of inactivity
  useAutoLogout();

  const location = useLocation();
  const userStatus = localStorage.getItem('user_status');
  const isSuspended = userStatus === 'Suspended';

  // If user is suspended and tries to access anything other than /suspended or /login,
  // redirect to the warning page. Always rendered through a normal Route.
  const ALLOWED_FOR_SUSPENDED = ['/suspended', '/login', '/'];
  if (isSuspended && !ALLOWED_FOR_SUSPENDED.includes(location.pathname)) {
    return <Navigate to="/suspended" replace />;
  }

  return (
    <>
      <Toaster position="top-center" />
      <ErrorBoundary>
        <MaintenanceCheck>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/setup-password" element={<SetupPassword />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/monthly-summary" element={<MonthlySummary />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/admincrud" element={<ErrorBoundary><AdminCRUD /></ErrorBoundary>} />
            <Route path="/reports" element={<ErrorBoundary><ReportsDashboard /></ErrorBoundary>} />
            <Route path="/workflow" element={<ErrorBoundary><WorkflowPage /></ErrorBoundary>} />
            <Route path="/suspended" element={<SuspendedNotice />} />
            <Route path="/restricted" element={<AccessRestricted />} />
            <Route path="/help" element={<Help />} />
            <Route path="/print/:claimId" element={<PrintBill />} />
            <Route path="/print-visit/:visitId" element={<PrintVisitSummary />} />
            <Route path="/print-discharge/:visitId" element={<PrintDischargeLetter />} />
            <Route path="/print-referral/:visitId" element={<PrintReferralLetter />} />
            <Route path="/print-preauth/:code" element={<PrintPreauthForm />} />
            <Route path="/maintenance" element={<Maintenance />} />
          </Routes>
        </MaintenanceCheck>
      </ErrorBoundary>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;